import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { StoreFinder } from "@/components/grocery/StoreFinder";
import { ShoppingCart, Carrot, Fish, Milk, Croissant, Package, Boxes, Lightbulb } from "lucide-react";

const AISLE_ICONS = { produce: Carrot, protein: Fish, dairy: Milk, bakery: Croissant, pantry: Package, other: Boxes };

export default function Grocery() {
  const { t, tv } = useLang();
  const u = (x) => (x === "pc" ? t("dm_unit_pc") : x === "slice" ? t("dm_unit_slice") : x);
  const [checked, setChecked] = useState({});
  const { data } = useQuery({ queryKey: ["grocery"], queryFn: async () => (await api.get("/meal-plan/grocery-list")).data });

  const toggle = (id) => setChecked((p) => ({ ...p, [id]: !p[id] }));

  return (
    <AppShell>
      <div className="flex items-center gap-3 chenia-fade-up">
        <span className="grid place-items-center h-11 w-11 rounded-2xl bg-primary text-primary-foreground"><ShoppingCart className="h-5 w-5" /></span>
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight">{t("grocery_title")}</h1>
          <p className="text-muted-foreground">{t("grocery_sub")}</p>
        </div>
      </div>

      <StoreFinder />

      {!data?.exists ? (
        <div className="chenia-card p-8 mt-8 text-center" data-testid="grocery-empty">
          <p className="text-muted-foreground">{t("grocery_empty")}</p>
          <Link to="/meal-plan"><Button className="mt-4 rounded-full bg-primary hover:bg-chenia-terracottadark">{t("nav_plan")}</Button></Link>
        </div>
      ) : (
        <>
          <p className="mt-5 text-sm text-muted-foreground" data-testid="grocery-count">{data.total_items} {t("grocery_items")} · {data.total_packs} {t("packs")}</p>
          <div className="grid md:grid-cols-2 gap-5 mt-3">
            {data.aisles.map((a) => {
              const Icon = AISLE_ICONS[a.aisle] || Boxes;
              return (
                <div key={a.aisle} className="chenia-card p-6" data-testid={`grocery-aisle-${a.aisle}`}>
                  <div className="flex items-center gap-2 mb-4">
                    <span className="grid place-items-center h-9 w-9 rounded-xl bg-secondary text-primary"><Icon className="h-4 w-4" /></span>
                    <h2 className="font-semibold">{t(`aisle_${a.aisle}`)}</h2>
                    <span className="text-xs text-muted-foreground ml-auto">{a.items.length}</span>
                  </div>
                  <ul className="space-y-1">
                    {a.items.map((item, i) => {
                      const id = `${a.aisle}-${i}`;
                      return (
                        <li key={id} className="flex items-start gap-3 py-1.5" data-testid={`grocery-row-${item.key}`}>
                          <Checkbox id={id} checked={!!checked[id]} onCheckedChange={() => toggle(id)} data-testid={`grocery-item-${id}`} className="mt-1" />
                          <label htmlFor={id} className={`flex-1 cursor-pointer ${checked[id] ? "line-through text-muted-foreground" : ""}`}>
                            <div className="flex justify-between gap-2">
                              <span>{tv(item.name)}</span>
                              <span className="text-sm font-medium shrink-0">{item.packs} × {item.pack_size} {u(item.unit)}</span>
                            </div>
                            <div className="text-[11px] text-muted-foreground">
                              {item.qty} {u(item.unit)} · {item.leftover > 0 ? `${t("leftover")} ${item.leftover} ${u(item.unit)}${item.keeps ? ` (${t("keeps")})` : ""}` : "—"}
                            </div>
                            {item.used_in?.length > 0 && (
                              <div className="text-[11px] text-muted-foreground/80 mt-0.5" data-testid={`grocery-used-in-${item.key}`}>
                                {t("used_in")}: {item.used_in.map((u) => tv(u.name)).join(", ")}
                              </div>
                            )}
                            {item.reuse_ideas?.length > 0 && (
                              <div className="mt-1 flex flex-wrap items-center gap-1" data-testid={`grocery-reuse-${item.key}`}>
                                <Lightbulb className="h-3 w-3 text-chenia-terracottadark" />
                                <span className="text-[11px] text-chenia-terracottadark">{t("leftover_idea")}:</span>
                                {item.reuse_ideas.map((r) => (
                                  <Link key={r.recipe_id} to={`/recipe/${r.recipe_id}`} onClick={(e) => e.stopPropagation()}
                                    className="text-[11px] rounded-full bg-chenia-rosesoft/70 px-2 py-0.5 hover:bg-chenia-rosesoft transition-colors">
                                    {tv(r.name)} · {r.qty} {u(item.unit)}
                                  </Link>
                                ))}
                              </div>
                            )}
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        </>
      )}
    </AppShell>
  );
}
